import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { API_V1_BASE_URL } from "../config/api";

const PASSWORD_RULES = {
  minLength: (password: string) => password.length >= 12,
  uppercase: (password: string) => /[A-Z]/.test(password),
  lowercase: (password: string) => /[a-z]/.test(password),
  number: (password: string) => /\d/.test(password),
  special: (password: string) => /[@$!%*?&]/.test(password),
};

interface ResetPasswordResponse {
  success: boolean;
  message?: string;
}

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const passwordValidation = {
    minLength: PASSWORD_RULES.minLength(newPassword),
    uppercase: PASSWORD_RULES.uppercase(newPassword),
    lowercase: PASSWORD_RULES.lowercase(newPassword),
    number: PASSWORD_RULES.number(newPassword),
    special: PASSWORD_RULES.special(newPassword),
  };

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (!isPasswordValid) {
      setError(
        "Password must contain minimum 12 characters with uppercase, lowercase, number and special character.",
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match");
      return;
    }

    setIsLoading(true);
    try {
      const token = new URLSearchParams(location.search).get("accessToken");

      if (!token) {
        throw new Error("Invalid reset link");
      }

      const response = await fetch(`${API_V1_BASE_URL}/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          newPassword,
        }),
      });

      const result = (await response
        .json()
        .catch(() => null)) as ResetPasswordResponse | null;

      if (!response.ok || !result?.success) {
        throw new Error(
          result?.message || "Unable to reset password. Please try again.",
        );
      }

      navigate("/login", { state: { resetSuccess: true } });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Unable to reset password. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-blue-50 to-gray-100">
      {/* Left Side - Branding Section */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-900 to-blue-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <h1 className="text-5xl font-bold mb-4">Welcome to SIMB</h1>
          <p className="text-xl mb-8">Multi-Tenant Platform Management</p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-white rounded-full"></div>
              <span>Centralized Platform Control</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-white rounded-full"></div>
              <span>Multi-Tenant Management</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-white rounded-full"></div>
              <span>White-Label Solutions</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Reset Password Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-3xl font-bold text-blue-900">SIMB</h1>
            <p className="text-gray-600 mt-2">Multi-Tenant Platform</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800">
                Set a New Password
              </h2>
              <p className="text-gray-600 mt-2">
                This is your first sign-in. Please replace your temporary
                password to continue.
              </p>
            </div>

            <form onSubmit={handleReset} className="space-y-6">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />

                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <div className="text-xs mt-2 space-y-1">
                  <p
                    className={
                      passwordValidation.minLength
                        ? "text-green-600"
                        : "text-gray-500"
                    }
                  >
                    {passwordValidation.minLength ? "✓" : "○"} Minimum 12
                    characters
                  </p>

                  <p
                    className={
                      passwordValidation.uppercase
                        ? "text-green-600"
                        : "text-gray-500"
                    }
                  >
                    {passwordValidation.uppercase ? "✓" : "○"} One uppercase
                    letter
                  </p>

                  <p
                    className={
                      passwordValidation.lowercase
                        ? "text-green-600"
                        : "text-gray-500"
                    }
                  >
                    {passwordValidation.lowercase ? "✓" : "○"} One lowercase
                    letter
                  </p>

                  <p
                    className={
                      passwordValidation.number
                        ? "text-green-600"
                        : "text-gray-500"
                    }
                  >
                    {passwordValidation.number ? "✓" : "○"} One number
                  </p>

                  <p
                    className={
                      passwordValidation.special
                        ? "text-green-600"
                        : "text-gray-500"
                    }
                  >
                    {passwordValidation.special ? "✓" : "○"} One special
                    character (@$!%*?&)
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-4 py-2 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                      confirmPassword && newPassword !== confirmPassword
                        ? "border-red-400"
                        : "border-gray-300"
                    }`}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />

                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </button>
                </div>
                {confirmPassword && (
                  <div className="mt-2 space-y-1">
                    {newPassword !== confirmPassword && (
                      <p className="text-xs text-red-500">
                        ❌ Passwords do not match
                      </p>
                    )}

                    {newPassword === confirmPassword && isPasswordValid && (
                      <p className="text-xs text-green-600">
                        ✅ Passwords match and meet security requirements
                      </p>
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={
                  isLoading ||
                  !isPasswordValid ||
                  newPassword !== confirmPassword
                }
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </div>

          <p className="mt-8 text-center text-xs text-gray-500">
            Secured by enterprise-grade encryption
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
